import { computed, onScopeDispose, ref, watch, type Ref } from 'vue'
import { useState } from '#app'
import {
    useInfiniteQuery,
    useQuery,
    useQueryClient,
    type InfiniteData
} from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { AwesomeGraphQLClient } from 'awesome-graphql-client'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql'
import { graphql, getFragmentData } from '~/resources/graphql'
import type { CpxInboxMessageFragment } from '~/resources/graphql/graphql'
import { useRpQuery } from '~/composables/graphql'
import { useViewer } from '~/composables/use-login'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useRouteLink } from '~/composables/routing'
import { useOrganizationContext } from '~/composables/use-organization-context'
import { useCurrentDate } from '~/composables/use-current-date'
import { parseUtc } from '~/composables/support/localization'
import { useMobileAppData } from './mobile-app-data'
import { usePostMessageCapacitor } from './post-message'

const inboxMessageFragment = graphql(`
    fragment CpxInboxMessage on MobileInboxMessage {
        id
        trackingReference
        notificationTitle
        notificationBody
        inboxHtml
        heroImageUrl
        cta {
            label
            target
        }
        read
        archived
        createdAt
        expiresAt
    }
`)

export type InboxMessage = CpxInboxMessageFragment

export type InboxEdge = {
    node: InboxMessage
    // Freeze the fetched group outside the mutable message so later read/expiry changes do not move its card.
    past: boolean
}

type InboxPage = {
    edges: InboxEdge[]
    pageInfo: {
        hasNextPage: boolean
        endCursor: string | null
    }
}

type InboxCursor = string | null

export type InboxNotification = {
    trackingReference: string | null
    clientEventId: string
}

/**
 * Return an initializer for the customer's unread badge, preserving any existing value.
 * Call after setting the viewer so synchronous session cleanup has finished.
 */
export function useInitializeMobileInboxUnread() {
    const queryClient = useQueryClient()

    /** Initialize the badge from viewer data without an additional startup request. */
    function initializeMobileInboxUnread(
        profileId: string,
        primaryCustomerId: string,
        hasUnread: boolean
    ): void {
        const unreadQueryKey = [
            'mobileInbox',
            `${profileId}:${primaryCustomerId}`,
            'unread'
        ]
        // Reused viewer snapshots must not overwrite newer read/unread changes already in that cache.
        if (queryClient.getQueryData(unreadQueryKey) === undefined) {
            queryClient.setQueryData(unreadQueryKey, hasUnread)
        }
    }

    return initializeMobileInboxUnread
}

/**
 * Reuse the viewer's initial unread flag and refresh only that field, never message content.
 * Leaving the menu makes its next mount refresh without adding a second startup request.
 */
export function useMobileInboxUnread() {
    const context = useInboxContext()
    onScopeDispose(() => {
        // Native profile layouts reuse the viewer, so a later menu visit must recheck the badge.
        void context.client.invalidateQueries({
            queryKey: ['mobileInbox', context.owner.value, 'unread'],
            refetchType: 'none'
        })
    })
    return useQuery({
        queryKey: computed(() => [
            'mobileInbox',
            context.owner.value,
            'unread'
        ]),
        enabled: context.enabled,
        staleTime: Infinity,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
        throwOnError: false,
        queryFn: async () => {
            const result = await context.quietQuery(
                graphql(`
                    query CpxInboxHasUnread {
                        viewer {
                            hasUnreadInboxMessages
                        }
                    }
                `)
            )
            return result.viewer?.hasUnreadInboxMessages ?? false
        }
    })
}

/**
 * Page through unread and today's read messages together, newest first, then past messages.
 * Retain fetched positions and sections until a fresh inbox visit, including across home-facility midnight.
 */
export function useMobileInboxList() {
    const context = useInboxContext()
    const viewer = useViewer()
    const currentDate = useCurrentDate()
    // Inbox ownership and its calendar follow the primary customer's home, never the selected family member.
    const homeFacilityTimezone = computed(
        () => viewer.value?.homeFacility.timezone ?? 'UTC'
    )
    const inboxQuery = useInfiniteQuery({
        queryKey: computed(() => ['mobileInbox', context.owner.value, 'list']),
        enabled: context.enabled,
        initialPageParam: null as InboxCursor,
        staleTime: Infinity,
        // Preserve long detail visits while still releasing abandoned lists.
        gcTime: 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
        throwOnError: false,
        queryFn: async ({ pageParam }): Promise<InboxPage> => {
            const result = await context.query(
                graphql(`
                    query CpxInboxPage($after: String) {
                        mobileInbox(first: 25, after: $after) {
                            edges {
                                node {
                                    past
                                    ...CpxInboxMessage
                                }
                            }
                            pageInfo {
                                hasNextPage
                                endCursor
                            }
                        }
                    }
                `),
                { after: pageParam }
            )
            return {
                ...result.mobileInbox,
                edges: result.mobileInbox.edges.map(edge => {
                    return {
                        ...edge,
                        node: getFragmentData(inboxMessageFragment, edge.node),
                        past: edge.node.past
                    }
                })
            }
        },
        getNextPageParam(page): InboxCursor | undefined {
            // Connection metadata remains authoritative even when source content removes every visible edge.
            if (page.pageInfo.hasNextPage) {
                return page.pageInfo.endCursor
            }
            return undefined
        }
    })
    return {
        ...inboxQuery,
        currentDate,
        homeFacilityTimezone
    }
}

/** Load one owned message; null is a normal unavailable-message result. */
export function useMobileInboxMessage(id: Ref<string>) {
    const context = useInboxContext()
    return useQuery({
        queryKey: computed(() => [
            'mobileInbox',
            context.owner.value,
            'message',
            id.value
        ]),
        enabled: computed(() => context.enabled.value && id.value !== ''),
        // Reuse a just-resolved push body, but recheck availability when returning to older details.
        staleTime: 5000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
        throwOnError: false,
        queryFn: async () => {
            const result = await context.query(
                graphql(`
                    query CpxInboxMessage($id: ID!) {
                        mobileInboxMessage(id: $id) {
                            ...CpxInboxMessage
                        }
                    }
                `),
                { id: id.value }
            )
            return getFragmentData(
                inboxMessageFragment,
                result.mobileInboxMessage
            )
        }
    })
}

/** Clear customer content and invalidate pending responses when the authenticated session changes. */
export function useMobileInboxSession() {
    const context = useInboxContext()
    const scroll = useState<{ profileId: string | null; top: number }>(
        'mobile-inbox-scroll',
        () => ({ profileId: null, top: 0 })
    )
    const failedNotification = useState<InboxNotification | null>(
        'mobile-inbox-failed-notification',
        () => null
    )
    const retryingNotification = useState(
        'mobile-inbox-retrying-notification',
        () => false
    )
    const activeNotificationId = useState<string | null>(
        'mobile-inbox-active-notification',
        () => null
    )
    watch(
        [context.owner, context.enabled],
        () => {
            context.generation.value++
            void context.client.cancelQueries({ queryKey: ['mobileInbox'] })
            context.client.removeQueries({ queryKey: ['mobileInbox'] })
            context.readStateVersions.value = {}
            scroll.value = { profileId: null, top: 0 }
            failedNotification.value = null
            retryingNotification.value = false
            activeNotificationId.value = null
        },
        { flush: 'sync' }
    )
}

/** Record inbox interactions and resolve authenticated notification destinations independently of native reporting. */
export function useMobileInboxActions() {
    const context = useInboxContext()
    const { isMobileApp } = useLayoutMode()
    const router = useRouter()
    const organization = useOrganizationContext()
    const { profileInboxLink, profileInboxMessageLink } = useRouteLink()
    const { postInitialNavigationComplete, postOpenExternalUrl } =
        usePostMessageCapacitor()
    const markingUnreadIds = ref(new Set<string>())
    const failedNotification = useState<InboxNotification | null>(
        'mobile-inbox-failed-notification',
        () => null
    )
    const retryingNotification = useState(
        'mobile-inbox-retrying-notification',
        () => false
    )
    const activeNotificationId = useState<string | null>(
        'mobile-inbox-active-notification',
        () => null
    )

    watch(context.generation, () => {
        markingUnreadIds.value.clear()
    })

    /**
     * Mark an unexpired message unread without recording another view.
     * Keep its existing content visible and leave read state unchanged if the request fails.
     */
    async function markUnread(message: InboxMessage): Promise<void> {
        if (
            !message.read ||
            message.archived ||
            parseUtc(message.expiresAt).getTime() <= Date.now() ||
            markingUnreadIds.value.has(message.id)
        )
            return

        const generation = context.generation.value
        // List and detail actions have separate composable instances but share this per-message sequence.
        const readStateVersion =
            (context.readStateVersions.value[message.id] ?? 0) + 1
        context.readStateVersions.value[message.id] = readStateVersion
        markingUnreadIds.value.add(message.id)
        try {
            const result = await context.query(
                graphql(`
                    mutation CpxMarkInboxUnread(
                        $input: SetMobileInboxMessageReadInput!
                    ) {
                        setMobileInboxMessageRead(input: $input) {
                            __typename
                            ... on SetMobileInboxMessageReadResult {
                                message {
                                    ...CpxInboxMessage
                                }
                            }
                        }
                    }
                `),
                { input: { id: message.id, read: false } }
            )
            if (
                generation !== context.generation.value ||
                readStateVersion !== context.readStateVersions.value[message.id]
            )
                return
            // Validation failures leave the existing card intact, rather than treating it as unavailable.
            if (
                result.setMobileInboxMessageRead.__typename !==
                'SetMobileInboxMessageReadResult'
            ) {
                throw new Error('Could not mark inbox message unread')
            }

            // Older detail/badge queries can still describe a read message. List snapshots
            // are canceled synchronously with the cache write inside updateMessage below.
            await Promise.all(
                [
                    ['mobileInbox', context.owner.value, 'unread'],
                    ['mobileInbox', context.owner.value, 'message', message.id]
                ].map(queryKey => context.client.cancelQueries({ queryKey }))
            )
            if (
                generation !== context.generation.value ||
                readStateVersion !== context.readStateVersions.value[message.id]
            )
                return

            const unread = getFragmentData(
                inboxMessageFragment,
                result.setMobileInboxMessageRead.message
            )
            if (unread !== null) {
                updateMessage(unread)
                if (!unread.read && !unread.archived) {
                    context.client.setQueryData(
                        ['mobileInbox', context.owner.value, 'unread'],
                        true
                    )
                }
            } else {
                // A message removed since the card loaded should disappear from the list too.
                context.client.setQueryData(
                    ['mobileInbox', context.owner.value, 'message', message.id],
                    null
                )
                await context.client.invalidateQueries({
                    queryKey: ['mobileInbox', context.owner.value, 'list']
                })
            }
        } catch {
            // The shared query client shows transport errors; rejected changes leave the card read and retryable.
        } finally {
            if (generation === context.generation.value)
                markingUnreadIds.value.delete(message.id)
        }
    }

    /**
     * Record a visible inbox preview or detail, marking the message read only when its detail is opened.
     * Reporting retries silently without counting a second first view.
     */
    async function recordView(
        message: InboxMessage,
        detail = true
    ): Promise<void> {
        const generation = context.generation.value
        // Only detail views change read state. Preview reporting must not supersede an explicit unread action.
        const readStateVersion = detail
            ? (context.readStateVersions.value[message.id] ?? 0) + 1
            : null
        if (readStateVersion !== null)
            context.readStateVersions.value[message.id] = readStateVersion
        try {
            const result = await retryReporting(async () => {
                // A failed old view must not retry after the customer deliberately marks the message unread.
                if (
                    detail &&
                    readStateVersion !==
                        context.readStateVersions.value[message.id]
                )
                    throw new Error('Inbox view superseded')
                const response = await context.quietQuery(
                    graphql(`
                        mutation CpxRecordInboxView(
                            $input: RecordMobileInboxViewInput!
                        ) {
                            recordMobileInboxView(input: $input) {
                                __typename
                                ... on RecordMobileInboxViewResult {
                                    message {
                                        ...CpxInboxMessage
                                    }
                                }
                            }
                        }
                    `),
                    { input: { id: message.id, detail } }
                )
                // Only successful reporting can establish that a message is no longer available.
                if (
                    response.recordMobileInboxView.__typename !==
                    'RecordMobileInboxViewResult'
                ) {
                    throw new Error('Could not record inbox message view')
                }
                return response.recordMobileInboxView
            })
            // Preview reporting must not replace detail content or race a newer read-state update.
            if (
                generation !== context.generation.value ||
                !detail ||
                readStateVersion !== context.readStateVersions.value[message.id]
            )
                return
            const viewed = getFragmentData(inboxMessageFragment, result.message)
            if (viewed !== null) {
                updateMessage(viewed)
                // A boolean cannot be decremented: recheck whether any other current message is unread.
                await context.client.invalidateQueries({
                    queryKey: ['mobileInbox', context.owner.value, 'unread']
                })
            } else {
                context.client.setQueryData(
                    ['mobileInbox', context.owner.value, 'message', message.id],
                    null
                )
            }
        } catch {
            // Reporting trouble must not hide content that the customer already saw.
        }
    }

    /**
     * Open local CPX links in place and websites, email or phone links through the appropriate external handler.
     * Unsupported URL schemes are ignored; body-link navigation does not record an engagement.
     */
    async function openLink(target: string): Promise<void> {
        let destination: URL
        try {
            destination = new URL(target, window.location.href)
        } catch {
            return
        }
        if (['mailto:', 'tel:'].includes(destination.protocol)) {
            // Contact links belong to the device's apps, not the CPX router or a new browser tab.
            if (isMobileApp.value) {
                postOpenExternalUrl(destination.href)
            } else {
                window.location.assign(destination.href)
            }
            return
        }
        if (!['http:', 'https:'].includes(destination.protocol)) return
        // Authored storefront links use public facility domains, but the customer's session lives on the mobile host.
        const internalOrigins = [
            window.location.origin,
            ...organization.value.facilityPublicUrls.map(
                url => new URL(url).origin
            ),
            new URL(organization.value.mobileUrl).origin
        ]
        if (internalOrigins.includes(destination.origin)) {
            await router.push(
                destination.pathname + destination.search + destination.hash
            )
        } else if (isMobileApp.value) {
            postOpenExternalUrl(destination.href)
        } else {
            // Browser development does not have a native parent to open external links.
            window.open(destination.href, '_blank', 'noopener,noreferrer')
        }
    }

    /** Follow the separate action button immediately and report that one interaction independently. */
    function tapCta(message: InboxMessage): void {
        if (
            message.cta === null ||
            message.archived ||
            parseUtc(message.expiresAt).getTime() <= Date.now()
        )
            return
        void openLink(message.cta.target).catch(() => {
            // Router navigation failures leave the customer on the current message.
        })
        // Reuse the original event identity if delivery needs one retry; the server records the time.
        // Opaque 128-bit IDs work on iOS 15.0–15.3, which lack randomUUID.
        const clientEventId = Array.from(
            crypto.getRandomValues(new Uint8Array(16)),
            byte => byte.toString(16).padStart(2, '0')
        ).join('')
        const document = graphql(`
            mutation CpxRecordPushEngagement(
                $input: RecordPushEngagementInput!
            ) {
                recordPushEngagement(input: $input) {
                    __typename
                    ... on RecordPushEngagementResult {
                        success
                    }
                }
            }
        `)
        const variables = {
            input: {
                trackingReference: message.trackingReference,
                clientEventId,
                event: 'CTA_CLICKED' as const
            }
        }
        // Native analytics need only the signed reference, so never attach a bearer token or refresh the login.
        // Browser requests retain the shared client and its cookie-bound CSRF protection.
        const controller = isMobileApp.value ? new AbortController() : null
        const nativeClient =
            controller === null
                ? null
                : new AwesomeGraphQLClient<TypedDocumentNode>({
                      endpoint: '/graphql-public',
                      formatQuery: print,
                      fetchOptions: {
                          credentials: 'omit',
                          keepalive: true,
                          signal: controller.signal
                      }
                  })
        // Both native attempts share one budget, even if opening the destination suspends CPX.
        const timeout =
            controller === null
                ? undefined
                : setTimeout(() => controller.abort(), 8000)
        void retryReporting(async () => {
            const response =
                nativeClient === null
                    ? await context.quietQuery(document, variables)
                    : await nativeClient.request(document, variables)
            if (
                response.recordPushEngagement.__typename !==
                    'RecordPushEngagementResult' ||
                !response.recordPushEngagement.success
            ) {
                throw new Error('Could not record push engagement')
            }
        })
            .catch(() => {
                // The destination has already opened; a reporting failure does not reverse navigation.
            })
            .finally(() => {
                clearTimeout(timeout)
            })
    }

    /**
     * Resolve an owned inbox message after authentication; native has already recorded the notification tap.
     * Initial taps release native startup while message content loads, with Retry available on failure.
     */
    async function handleNotification(
        notification: InboxNotification,
        initial: boolean
    ): Promise<void> {
        // A newer native destination takes precedence over a slow request or an inbox-page retry.
        activeNotificationId.value = notification.clientEventId
        const generation = context.generation.value
        try {
            // Authentication is complete. Keep the existing skeleton visible while resolving
            // the message instead of letting slow content trip the native login timeout.
            if (initial) postInitialNavigationComplete()
            let message: InboxMessage | null = null
            if (notification.trackingReference !== null) {
                const result = await context.quietQuery(
                    graphql(`
                        query CpxInboxMessageForNotification(
                            $trackingReference: String!
                        ) {
                            mobileInboxMessageForNotification(
                                trackingReference: $trackingReference
                            ) {
                                ...CpxInboxMessage
                            }
                        }
                    `),
                    { trackingReference: notification.trackingReference }
                )
                message = getFragmentData(
                    inboxMessageFragment,
                    result.mobileInboxMessageForNotification
                )
            }
            if (
                generation !== context.generation.value ||
                activeNotificationId.value !== notification.clientEventId
            )
                return
            if (message === null) {
                await router.replace({
                    path: profileInboxLink(),
                    query: { unavailable: '1' }
                })
            } else {
                updateMessage(message)
                await router.replace(profileInboxMessageLink(message.id))
            }
            if (activeNotificationId.value === notification.clientEventId)
                failedNotification.value = null
        } catch {
            if (
                generation === context.generation.value &&
                activeNotificationId.value === notification.clientEventId
            ) {
                // A transport error is not evidence of deletion; retain the destination for the ordinary Retry control.
                failedNotification.value = notification
                await router.replace(profileInboxLink())
            }
        }
    }

    /** Retry resolving the last failed inbox destination without recording another notification click. */
    async function retryNotification(): Promise<void> {
        if (failedNotification.value === null || retryingNotification.value)
            return
        const generation = context.generation.value
        retryingNotification.value = true
        try {
            await handleNotification(failedNotification.value, false)
        } finally {
            if (generation === context.generation.value)
                retryingNotification.value = false
        }
    }

    /** Synchronize read state while preserving content omitted from action responses. */
    function updateMessage(message: InboxMessage): void {
        // Infinite queries retain a snapshot of every loaded page while fetching more. TanStack
        // cancels/reverts synchronously, so write immediately before the sentinel can resume that
        // same cursor on Vue's next tick. Awaiting cancellation here would reopen the stale-page race.
        // An empty initial load has no cached cards to update and must remain free to finish.
        void context.client.cancelQueries({
            queryKey: ['mobileInbox', context.owner.value, 'list'],
            predicate: query => query.state.data !== undefined
        })
        context.client.setQueryData<InboxMessage | null>(
            ['mobileInbox', context.owner.value, 'message', message.id],
            previous => {
                // A list action returns only a summary. Opening detail must still fetch its
                // body when no full message is cached, rather than treating that summary as fresh.
                if (previous === undefined && message.inboxHtml === null)
                    return undefined
                return {
                    ...message,
                    inboxHtml: message.inboxHtml ?? previous?.inboxHtml ?? null,
                    heroImageUrl:
                        message.heroImageUrl ?? previous?.heroImageUrl ?? null
                }
            }
        )
        context.client.setQueriesData<InfiniteData<InboxPage, InboxCursor>>(
            { queryKey: ['mobileInbox', context.owner.value, 'list'] },
            previous => {
                if (previous === undefined) return undefined
                return {
                    ...previous,
                    pages: previous.pages.map(page => ({
                        ...page,
                        edges: page.edges.map(edge => {
                            if (edge.node.id !== message.id) return edge
                            // Update every cached copy without changing its connection metadata or content.
                            return {
                                ...edge,
                                node: {
                                    ...edge.node,
                                    ...message,
                                    inboxHtml:
                                        message.inboxHtml ??
                                        edge.node.inboxHtml,
                                    heroImageUrl:
                                        message.heroImageUrl ??
                                        edge.node.heroImageUrl
                                }
                            }
                        })
                    }))
                }
            }
        )
    }

    /** A bounded retry is enough for online CPX; no background queue or offline cache is introduced. */
    async function retryReporting<Result>(
        operation: () => Promise<Result>
    ): Promise<Result> {
        const generation = context.generation.value
        try {
            return await operation()
        } catch (error) {
            if (generation !== context.generation.value) throw error
            return await operation()
        }
    }

    return {
        markUnread,
        markingUnreadIds,
        recordView,
        openLink,
        tapCta,
        handleNotification,
        failedNotification,
        retryingNotification,
        retryNotification
    }
}

/** Share the primary-profile identity and authenticated clients across inbox queries and actions. */
function useInboxContext() {
    const viewer = useViewer()
    const { isMobileApp, isInboxEnabled } = useLayoutMode()
    const mobileAppData = useMobileAppData()
    const owner = computed(() =>
        viewer.value === undefined
            ? null
            : `${viewer.value.id}:${viewer.value.customerId}`
    )
    const enabled = computed(
        () =>
            process.client &&
            isInboxEnabled.value &&
            owner.value !== null &&
            // Browser sessions use the normal CPX cookie; only the native shell needs a bearer token.
            (!isMobileApp.value || mobileAppData.value.bearerToken !== null)
    )
    return {
        owner,
        enabled,
        generation: useState('mobile-inbox-generation', () => 0),
        // Plain serializable values let list/detail actions reject obsolete responses across component remounts.
        readStateVersions: useState<Record<string, number>>(
            'mobile-inbox-read-state-versions',
            () => ({})
        ),
        client: useQueryClient(),
        query: useRpQuery({ orgLevel: true }),
        quietQuery: useRpQuery({ orgLevel: true, silent: true })
    }
}
